export const SORTABLE_INITIAL_STATE = {
    isOpen: false,
    data: [] as any[],
};

export const BULK_SELECTION_INITIAL_STATE = {
    isOpen: false,
    data: [] as number[],
};

export const INVOICE_TABLE_ALIAS = 'cp_invoice';
export const POD_TABLE_ALIAS = 'cp_pod';

// "Days Outstanding" filter buckets — days PAST DUE on unpaid invoices. Same values as WAL's
// Receivables DAYS_OVERDUE_BUCKET_OPTIONS so both apps read the same aging the same way.
export const DAYS_OVERDUE_BUCKET_OPTIONS = [
    { label: '1-30 days', value: '1-30' },
    { label: '31-60 days', value: '31-60' },
    { label: '61-90 days', value: '61-90' },
    { label: '90+ days', value: '90+' },
];

export const INVOICE_DEFAULT_COLUMNS = [
    // Renders as a "View Invoice" link (client request 2026-09-15) — the invoice number itself
    // is shown in the link tooltip and in the modal title.
    { id: 'invoiceNumber', name: 'Invoice', visible: true, displayOrder: 1 },
    { id: 'orderId', name: 'Order ID', visible: true, displayOrder: 2 },
    { id: 'status', name: 'Status', visible: true, displayOrder: 4 },
    { id: 'dueDate', name: 'Due Date', visible: true, displayOrder: 5 },
    { id: 'dateInvoiced', name: 'Date Invoiced', visible: true, displayOrder: 6 },
    { id: 'lastUpdated', name: 'Last Updated', visible: true, displayOrder: 7 },
    { id: 'totalPrice', name: 'Total Price', visible: true, displayOrder: 8 },
    { id: 'paymentTerms', name: 'Payment Terms', visible: true, displayOrder: 9 },
    // Days Pending column hidden per request — restore by uncommenting this line AND
    // removing the daysPending filter in AppLayout's setInvoiceColumns merge.
    // { id: 'daysPending', name: 'Days Pending', visible: true, displayOrder: 10 },
    { id: 'pod', name: 'POD', visible: true, displayOrder: 10 },
    // Newest upload date across the row's visible PODs — sits beside POD so the two read together.
    { id: 'podUploadedAt', name: 'POD Upload Date', visible: true, displayOrder: 11 },
    // No trailing action column: the per-row Download icon was removed (client request 2026-09-16).
];

export const POD_DEFAULT_COLUMNS = [
    { id: 'fileName', name: 'File Name', visible: true, displayOrder: 1 },
    { id: 'orderId', name: 'Order ID', visible: true, displayOrder: 2 },
    // Order-derived columns — same values (and same cell rendering) as the Orders listing.
    { id: 'origin', name: 'Origin', visible: true, displayOrder: 3 },
    { id: 'destination', name: 'Destination', visible: true, displayOrder: 4 },
    { id: 'deliveryDate', name: 'Delivery Date', visible: true, displayOrder: 5 },
    { id: 'carrier', name: 'Carrier', visible: true, displayOrder: 6 },
    { id: 'dateUploaded', name: 'Last Updated', visible: true, displayOrder: 7 },
    { id: 'action', name: '', visible: true, displayOrder: 8 },
];

export const INVOICE_COLUMN_CLASSNAME_MAP: Record<string, string> = {
    invoiceNumber: 'max-w-[150px] min-w-[150px]',
    orderId: 'max-w-[150px] min-w-[150px]',
    status: 'max-w-[150px] min-w-[150px]',
    dueDate: 'max-w-[160px] min-w-[160px]',
    dateInvoiced: 'max-w-[150px] min-w-[150px]',
    lastUpdated: 'max-w-[150px] min-w-[150px]',
    totalPrice: 'max-w-[150px] min-w-[150px]',
    paymentTerms: 'max-w-[150px] min-w-[150px]',
    daysPending: 'max-w-[150px] min-w-[150px]',
    pod: 'min-w-[150px] max-w-[150px]',
    podUploadedAt: 'max-w-[160px] min-w-[160px]',
    action: 'w-[40px] min-w-[40px] max-w-[40px] sticky right-0 bg-inherit',
};

export const POD_COLUMN_CLASSNAME_MAP: Record<string, string> = {
    fileName: 'max-w-[240px] min-w-[240px] !pr-4',
    orderId: 'max-w-[150px] min-w-[150px]',
    origin: 'max-w-[165px] min-w-[165px]',
    destination: 'max-w-[165px] min-w-[165px]',
    deliveryDate: 'min-w-[155px] max-w-[155px]',
    carrier: 'min-w-[180px]  max-w-[180px]',
    dateUploaded: 'max-w-[200px] min-w-[200px]',
    action: 'w-[40px] min-w-[40px] max-w-[40px] sticky right-0 bg-inherit',
};

export const INVOICE_COLUMN_TRANSLATION_MAP: Record<string, string> = {
    invoiceNumber: 'DocumentHub.Invoice',
    orderId: 'Common.Order_ID',
    status: 'Common.Status',
    dueDate: 'DocumentHub.Due_Date',
    dateInvoiced: 'DocumentHub.Date_Invoiced',
    lastUpdated: 'DocumentHub.Last_Updated',
    totalPrice: 'DocumentHub.Total_Price',
    paymentTerms: 'DocumentHub.Payment_Terms',
    daysPending: 'DocumentHub.Days_Pending',
    pod: 'DocumentHub.POD',
    podUploadedAt: 'DocumentHub.POD_Upload_Date',
    action: '',
};

export const POD_COLUMN_TRANSLATION_MAP: Record<string, string> = {
    fileName: 'Common.File_Name',
    orderId: 'Common.Order_ID',
    origin: 'Common.Origin',
    destination: 'Common.Destination',
    deliveryDate: 'Common.Delivery_Date',
    carrier: 'Common.Carrier',
    dateUploaded: 'DocumentHub.Last_Updated',
    action: '',
};

// Dummy data for invoices
export const dummyInvoicesData = [
    {
        id: 1,
        fileName: 'Invoice202.pdf',
        fileSize: 200,
        status: 'Paid',
        orderId: '#WAL-DMIF-1',
        dateInvoiced: '2024-01-04',
        lastUpdated: '2024-01-04',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 30',
        daysPending: 4,
    },
    {
        id: 2,
        fileName: 'Rate Confirmation Invoice.png',
        fileSize: 720,
        status: 'Paid',
        orderId: '#WAL-GHKS-23',
        dateInvoiced: '2024-01-08',
        lastUpdated: '2024-01-08',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 60',
        daysPending: 4,
    },
    {
        id: 3,
        fileName: 'Commercial Invoice.pdf',
        fileSize: 200,
        status: 'Overdue',
        orderId: '#WAL-LKPO-54',
        dateInvoiced: '2024-01-02',
        lastUpdated: '2024-01-12',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 45',
        daysPending: -2,
    },
    {
        id: 4,
        fileName: 'Invoice202.pdf',
        fileSize: 200,
        status: 'Pending',
        orderId: '#WAL-BVFR-12',
        dateInvoiced: '2024-01-06',
        lastUpdated: '2024-01-02',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 30',
        daysPending: 4,
    },
    {
        id: 5,
        fileName: 'Invoice.png',
        fileSize: 500,
        status: 'Pending',
        orderId: '#WAL-HJKA-39',
        dateInvoiced: '2024-01-10',
        lastUpdated: '2024-01-06',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 45',
        daysPending: 4,
    },
    {
        id: 6,
        fileName: 'Customer Invoice.pdf',
        fileSize: 200,
        status: 'Paid',
        orderId: '#WAL-QWER-91',
        dateInvoiced: '2024-01-12',
        lastUpdated: '2024-01-10',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 30',
        daysPending: 4,
    },
    {
        id: 7,
        fileName: 'Customer Invoice.pdf',
        fileSize: 200,
        status: 'Overdue',
        orderId: '#WAL-JHTG-26',
        dateInvoiced: '2024-01-14',
        lastUpdated: '2024-01-14',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 30',
        daysPending: -1,
    },
    {
        id: 8,
        fileName: 'Customer Invoice.pdf',
        fileSize: 200,
        status: 'Overdue',
        orderId: '#WAL-BNHY-37',
        dateInvoiced: '2024-01-16',
        lastUpdated: '2024-01-06',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 45',
        daysPending: 4,
    },
    {
        id: 9,
        fileName: 'Financial report.xlsx',
        fileSize: 540,
        status: 'Paid',
        orderId: '#WAL-KHJX-45',
        dateInvoiced: '2024-01-18',
        lastUpdated: '2024-01-16',
        totalPrice: '$542.22 CAD',
        paymentTerms: 'Net 45',
        daysPending: 4,
    },
];

// Dummy data for POD documents
export const dummyPodData = [
    {
        id: 1,
        fileName: 'POD_002.pdf',
        fileSize: 200,
        orderId: '#WAL-DMIF-1',
        dateUploaded: '2024-01-04T10:30:00Z',
    },
    {
        id: 2,
        fileName: 'POD_002.png',
        fileSize: 720,
        orderId: '#WAL-GHKS-23',
        dateUploaded: '2024-01-08T14:15:00Z',
    },
    {
        id: 3,
        fileName: 'Commercial POD.pdf',
        fileSize: 200,
        orderId: '#WAL-LKPO-54',
        dateUploaded: '2024-01-02T09:45:00Z',
    },
    {
        id: 4,
        fileName: 'POD_002.pdf',
        fileSize: 200,
        orderId: '#WAL-BVFR-12',
        dateUploaded: '2024-01-06T16:20:00Z',
    },
    {
        id: 5,
        fileName: 'POD_002.png',
        fileSize: 500,
        orderId: '#WAL-HJKA-39',
        dateUploaded: '2024-01-10T11:30:00Z',
    },
    {
        id: 6,
        fileName: 'POD_002.pdf',
        fileSize: 200,
        orderId: '#WAL-QWER-91',
        dateUploaded: '2024-01-12T13:45:00Z',
    },
    {
        id: 7,
        fileName: 'POD_002.png',
        fileSize: 300,
        orderId: '#WAL-JHTG-26',
        dateUploaded: '2024-01-14T08:15:00Z',
    },
    {
        id: 8,
        fileName: 'POD_002.pdf',
        fileSize: 100,
        orderId: '#WAL-BNHY-37',
        dateUploaded: '2024-01-16T17:30:00Z',
    },
    {
        id: 9,
        fileName: 'POD_002.pdf',
        fileSize: 300,
        orderId: '#WAL-KJHX-45',
        dateUploaded: '2024-01-18T12:00:00Z',
    },
];
